import type {
  ClientMetricsView,
  CommunicationMetricsParams,
  CommunicationMetricsView,
  HealthBoxMetricsParams,
  HealthBoxMetricsView,
  MetricsRangeParams,
  OperationMetricsParams,
  OperationMetricsView,
  RevenueMetricsParams,
  RevenueMetricsView,
} from "@/types/metrics";
import { apiUrl } from "@/utils/url";
import { getErrorMessage } from "../errors";
import { HttpClient } from "../HttpClient";
import type { IMetricsService } from "./IMetricsService";
import { isAbortError } from "../errors/isAbortError";

/**
 * The range every section shares.
 *
 * Both dates are required by the API. `storeId` is left out only when the
 * caller asks for every store, which is what the backend aggregates without
 * it.
 */
const buildRangeQuery = (
  { storeId, startDate, endDate }: MetricsRangeParams,
  extra: Record<string, string | number | undefined> = {},
): URLSearchParams => {
  const params = new URLSearchParams({ startDate, endDate });

  for (const [key, value] of Object.entries({ storeId, ...extra })) {
    if (value !== undefined) params.set(key, String(value));
  }

  return params;
};

export class MetricsService implements IMetricsService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      timeout: 10000,
      headers: {},
    });
  }

  /**
   * One request, with the abort rethrown untouched.
   *
   * A cancelled section must not paint an error: the page aborts in flight
   * whenever the date range changes, and every one of those would otherwise
   * land as a load failure.
   */
  private async load<T>(
    endpoint: string,
    params: URLSearchParams,
    fallbackMessage: string,
    signal?: AbortSignal,
  ): Promise<T> {
    try {
      return await this.httpClient.get<T>(
        `${endpoint}?${params.toString()}`,
        signal ? { signal } : {},
      );
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new Error(getErrorMessage(error, fallbackMessage));
    }
  }

  getClientMetrics(
    params: MetricsRangeParams,
    signal?: AbortSignal,
  ): Promise<ClientMetricsView> {
    return this.load<ClientMetricsView>(
      "/metrics/dashboard/clients",
      buildRangeQuery(params),
      "Não foi possível carregar as métricas de clientes. Atualize a página e tente novamente.",
      signal,
    );
  }

  getRevenueMetrics(
    { periodicityCode, rankSize, ...range }: RevenueMetricsParams,
    signal?: AbortSignal,
  ): Promise<RevenueMetricsView> {
    return this.load<RevenueMetricsView>(
      "/metrics/dashboard/revenues",
      buildRangeQuery(range, { periodicityCode, rankSize }),
      "Não foi possível carregar as métricas de receitas. Atualize a página e tente novamente.",
      signal,
    );
  }

  getHealthBoxMetrics(
    { rankSize, ...range }: HealthBoxMetricsParams,
    signal?: AbortSignal,
  ): Promise<HealthBoxMetricsView> {
    return this.load<HealthBoxMetricsView>(
      "/metrics/dashboard/health-box",
      buildRangeQuery(range, { rankSize }),
      "Não foi possível carregar as métricas HealthBox. Atualize a página e tente novamente.",
      signal,
    );
  }

  getCommunicationMetrics(
    { dayLimit, ...range }: CommunicationMetricsParams,
    signal?: AbortSignal,
  ): Promise<CommunicationMetricsView> {
    return this.load<CommunicationMetricsView>(
      "/metrics/dashboard/communications",
      buildRangeQuery(range, { dayLimit }),
      "Não foi possível carregar as métricas de comunicações. Atualize a página e tente novamente.",
      signal,
    );
  }

  getOperationMetrics(
    { serviceTypeId, rankSize, ...range }: OperationMetricsParams,
    signal?: AbortSignal,
  ): Promise<OperationMetricsView> {
    return this.load<OperationMetricsView>(
      "/metrics/dashboard/operations",
      buildRangeQuery(range, { serviceTypeId, rankSize }),
      "Não foi possível carregar as métricas de operações. Atualize a página e tente novamente.",
      signal,
    );
  }
}
